import styled from "styled-components";
import {CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX} from "./landing/campaignSpace";
import {campaignPalette} from "./landing/campaignPalette";
import type {CampaignId} from "./landing/campaignPlayback";
import {CREATE_CARD_SIDE_BY_SIDE_WIDTH, CREATE_CARD_WIDTH} from "./CreateFlow.styles";

/**
 * Externalized styles for {@link HomePage} (ui#170 reworked the hero layout chain, so its styles
 * moved out of the component per the repo's "styles live next to the component" convention).
 */

/**
 * The gutter the hero's RIGHT column keeps from the window, and therefore the card's own clearance.
 *
 * Named rather than typed twice since kitecorp/transferify-ui#2403, because a second reader arrived:
 * the recipient page's wallpaper line is laid out in the space that card leaves, so it has to begin
 * and end at the same gutter the card does. Two literals would drift into a line that starts one
 * pixel band away from the edge the card is aligned to, which is invisible in review and wrong.
 *
 * Pixels rather than a CSS string, so an arithmetic reader can add it to a card width in a `calc()`.
 */
export const HERO_SIDE_INSET_PX = 30;

/** The gap between the hero's two columns, and the gap the wallpaper line keeps from the card. */
export const HERO_COLUMN_GAP = "clamp(28px, 5vw, 72px)";

/**
 * The hero's bottom inset on the side-by-side band. Named because the landing surface subtracts it
 * from the cookie bar's reserve to find how much of the bar the hero does NOT already clear
 * (CampaignSurface.styles, ui#2596); a literal there would drift from this one in silence.
 */
export const HERO_BOTTOM_INSET_PX = 60;

/**
 * Design `.app`/`.stage` — the single screen. On viewports tall enough for the hero the content
 * fits exactly (no scrollbar); the design's `.stage` keeps `overflow-y:auto` as the escape hatch
 * so short-but-wide viewports (snapped windows, small laptops) can still reach the create card.
 *
 * `flex: 1; min-height: 0` rather than `height: 100%` since ui#2394 put a legal footer row at the foot
 * of the same scroller. A percentage height resolves against the WHOLE scroller, so this box would
 * have kept claiming the full window with the footer stacked under it — turning the single screen into
 * a screen plus the footer's own height of scroll, on every page that mounts this surface. Taking the
 * space that is left keeps the design's "fits exactly, no scrollbar" true; the `overflow-y` escape
 * hatch above is unchanged and still absorbs a window too short for the hero.
 *
 * The recipient surface carried this same pair in `CampaignSurface.styles` and does not any more: it
 * is stated once, here, because every surface mounting this box now needs it.
 */
export const Landing = styled.div<{$scene: CampaignId}>`
	position: relative;
	width: 100%;
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	overflow-x: hidden;
	text-align: left;
	background-color: ${({theme, $scene}) => campaignPalette(theme.tokens, $scene).ground};
`;

/** Fills the viewport so HeroLayout can centre its columns without changing their layout on login. */
export const LandingContent = styled.div`
	pointer-events: none;
	position: relative;
	z-index: 1;
	min-height: 100%;
	display: flex;
	flex-direction: column;
`;

/**
 * Fills the viewport but can grow beyond it on short windows, where Landing supplies scrolling.
 * HeroLayout centres the columns inside this box.
 *
 * Horizontal padding is 0 on desktop (ui#170): the {@link HeroLayout} owns the full-width band and
 * insets it 30px on each side to line up with the NavBar gutters, so the section itself must not add
 * any side gutter. Mobile keeps its 16px side padding (the create card is full-width there).
 */
export const Hero = styled.section`
	position: relative;
	display: flex;
	flex: 1 0 auto;
	padding: 78px 0 ${HERO_BOTTOM_INSET_PX}px;
	box-sizing: border-box;

	/* No min-height naming the window here (ui#2635). This used to carry
	   "&:not(:last-child) { min-height: 100dvh; }", from a time when the editorial was a SECTION after
	   the hero; it moved inside as EditorialColumn, so LandingContent has had one in-flow child since,
	   the rule matched nothing, and 100dvh would have been the window rather than the surface the moment
	   it did match again. The flex line above already does the job it was written for: LandingContent is
	   min-height: 100%, so flex: 1 0 auto grows this section into whatever room that box has and never
	   shrinks it below its content. If a following section is ever added, that is still the answer, and
	   nav/SiteFooter.styles surfaceHeight is the declaration to take if one is ever needed. */

	@media (max-width: ${CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX - 1}px) {
		padding: 78px 16px 44px;
	}
`;

/**
 * Design `.create-layout` — editorial left, sender-flow card right.
 *
 * ui#170: the band no longer centres at a 1200px max-width. Instead it STRETCHES to the full
 * viewport and insets a symmetric 30px gutter, so both columns anchor to the NavBar's gutters — the
 * editorial's left edge lines up with the brand mark (left 30px) and the create card's right edge
 * lines up with the links pill's right inset (right 30px, the Topbar's `0 30px`). `space-between`
 * then pushes the (max-width-capped) editorial to the left gutter and the fixed-width create card to
 * the right gutter. The editorial keeps its own readable measure (see LandingCampaign `max-width`), so
 * stretching the band never widens the running text — at ultra-wide the only thing that grows is the
 * neutral whitespace between the two anchored columns. Below 901px the band becomes ONE COLUMN —
 * create card first, editorial under it (ui#1106) — and the gutter drops to 0 (Hero supplies the
 * 16px mobile inset).
 *
 * <b>The band FILLS the hero rather than being centred inside it, and that is a layout-stability
 * fix rather than a look (ui#1796).</b> It used to size to its tallest column and centre itself with
 * `margin: auto`. The create card is one of those columns and it grows whenever the sender adds
 * files, so the band's own box — full viewport width, and several hundred pixels tall — was re-laid
 * out on every such change. Chrome scores that as an unforced layout shift: a file arrives from the
 * OS picker or a drop, neither of which is the "recent input" the layout-instability API excuses
 * within 500 ms, so the whole band counted as unstable. Measured at 1280x900, adding three files
 * scored CLS 0.174 with the band as the only reported source, against 0.055 for the card's own move
 * once the band stops moving. `e2e/landing-cls.spec.ts` holds that 0.08 line, and was watched red at
 * 0.174 against this file's previous revision.
 *
 * <b>Nothing rendered moves.</b> A flex item with no auto margins stretches to its container's cross
 * size, so the band becomes exactly the {@link Hero}'s content box, and `align-items: center` then
 * centres each column inside that taller box — which lands every child on the pixel it was already
 * on, because centring a short band and then centring its children in it is the same arithmetic as
 * centring the children in the whole box. That was measured rather than reasoned: the heading, the
 * lede, the guide link, the create card and the hero itself were read back at 1280x900, 1440x1080,
 * 901x700, 768x1024, 375x812 and 320x568, in both locales, at rest and with three files added, and
 * every one of those boxes is identical to the pixel across the change. The band's own box is the
 * only thing in the whole matrix that moved, which is exactly what this is for.
 *
 * The `justify-content: center` below is the mobile half of the same swap. `margin: auto` was what
 * centred the stacked column when the hero had room to spare, and `flex-start` only agreed with it
 * because the band was exactly as tall as its content. A filled band has to say so itself. It cannot
 * overflow upward the way a centred flex column normally can, because `min-height` (not `height`)
 * lets the band grow past the hero when the stack is taller than the screen — the case a phone with
 * files selected is always in.
 */
export const HeroLayout = styled.div`
	position: relative;
	z-index: 1;
	min-height: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${HERO_COLUMN_GAP};
	width: 100%;
	/* Asymmetric on desktop (ui#175): the RIGHT inset stays 30px so the create card's edge remains
	   aligned to the pill (ui#170 preserved), while the LEFT inset grows responsively so the editorial
	   text isn't jammed against the brand-mark gutter. The vw term is capped so the editorial keeps a
	   comfortable width even at 901px (≈63px left there, leaving the editorial its measure). */
	padding: 0 ${HERO_SIDE_INSET_PX}px 0 clamp(40px, 7vw, 140px);
	box-sizing: border-box;

	@media (max-width: ${CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX - 1}px) {
		padding: 0;
		flex-direction: column;
		align-items: stretch;
		/* Not space-between: on a viewport tall enough for both blocks it would park the editorial
		   against the bottom edge with a hole under the card. Centring is what keeps the pair centred
		   now that the band fills the hero instead of shrinking to the stack (see the note above);
		   flex-start here would raise both blocks by half the spare height.
		   (No backticks in a comment inside a styled-components literal - one would end the literal.) */
		justify-content: center;
		/* The stacked blocks bring their own vertical rhythm — the create flow's own bottom padding
		   under the card, then the display's margin-top — so the row gap would only double it. */
		gap: 0;
		/*
		 * Keyboard and screen-reader traversal follows the VISUAL order here, not the source order, so
		 * a tab out of the nav reaches the create panel before the guide link that now sits below the
		 * fold. Not Baseline (Chromium-only at the time of writing); where it is unsupported the page
		 * simply keeps its source order, which is a coherent reading order in its own right — heading,
		 * lede, link, then the form — so this is an upgrade with no fallback to write.
		 */
		reading-flow: flex-visual;
	}
`;

/**
 * Design `.editorial`. From 901px up it is `display: contents`, so the editorial block itself is the
 * flex item and the design's measure comes from `LandingCampaign`'s own `max-width`.
 *
 * Below 901px it becomes a real block and MOVES UNDER the create panel (ui#1106). It used to be
 * `display: none` there, which hid the page's `<h1>` and its one in-flow link to the head-term guide
 * from every phone visitor while the pre-rendered HTML still carried both — so mobile-first indexing
 * rendered a page with no visible heading, and the human half of that link did not exist. Owner
 * decision 2026-08-17: show them, after the create panel, which is what a phone visitor came for.
 *
 * It borrows the create card's measure and centring so the two stacked blocks share one left edge at
 * every width from 405px to 900px; below that both simply fill the Hero's 16px inset.
 */
export const EditorialColumn = styled.div`
	display: contents;

	@media (max-width: ${CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX - 1}px) {
		display: block;
		order: 2;
		width: 100%;
		max-width: ${CREATE_CARD_WIDTH};
		margin: 0 auto;
		min-width: 0;
	}
`;

/** Design `.cardwrap` — the sender-flow column (just the create card; the eyebrow above it was
 * dropped by owner decision in ui#54). */
export const FlowColumn = styled.div`
	pointer-events: auto;
	/* Beside the campaign; the card inside keeps its own 404px measure and max-width: 100% takes it
	   down to this. The stacked rule below and the recipient surface restate their own widths. */
	width: ${CREATE_CARD_SIDE_BY_SIDE_WIDTH};
	max-width: 100%;
	flex: none;
	display: flex;
	flex-direction: column;

	@media (max-width: ${CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX - 1}px) {
		width: 100%;
		/* Explicit, and paired with EditorialColumn's order:2 rather than leaning on the default 0:
		   the two numbers are the whole of ui#1106's stacking rule, so they are spelled together and
		   a third child added later cannot land between them by accident. */
		order: 1;
	}
`;
